import { CUSTOMER_QUESTIONS_FLAG_KEY } from '@aica/core';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import type { QuestionsSwitch } from './VoiceSiteTab';

/**
 * `enableCustomerQuestions` as the client knows it (`QuestionsSwitch`).
 *
 * - Portal: the server's verdict for it in the flag map (`gates`) — and only
 *   that. The flag is sold from a tier up, so its boolean is never read
 *   (`FeatureFlagsContext`): a verdict that is `failed` — a failure, a
 *   degraded answer, an answer without this verdict — is `unknown`, and the
 *   server decides.
 * - Bitrix24 (the shell hands `access`): the session carries the flag itself
 *   (#1347, #1605) and no verdicts; the boolean of a session that answered is
 *   the answer, a failed session is `unknown`.
 */
export function useQuestionsSwitch(
  sessionShell: boolean,
  lockedDemo: boolean
): QuestionsSwitch {
  const flags = useFeatureFlagsSafe();
  // On the demo of a closed section (#2816) a plan below the blocks' own is not
  // «upgrade»: the server answers the demo with whatever blocks it has, and a
  // closed one comes back without them.
  if (lockedDemo && flags.gate(CUSTOMER_QUESTIONS_FLAG_KEY) === 'tier_not_eligible') {
    return 'unknown';
  }
  if (sessionShell) {
    if (flags.error !== null) return 'unknown';
    if (!flags.isReady) return 'loading';
    return flags.isEnabled(CUSTOMER_QUESTIONS_FLAG_KEY) ? 'on' : 'off';
  }
  const verdict = flags.gate(CUSTOMER_QUESTIONS_FLAG_KEY);
  if (verdict === 'enabled') return 'on';
  if (verdict === 'feature_disabled') return 'off';
  if (verdict === 'tier_not_eligible') return 'upgrade';
  if (verdict === 'loading') return 'loading';
  return 'unknown';
}
